import { makeAutoObservable, runInAction } from "mobx";
import { showToast } from "@/lib/utils/toast";
import { companyService } from "../services";
import { authStore } from "@/features/auth";
import { validateCompanyFormStep } from "../schemas";

const STORAGE_KEY = "nxstart.onboard.v1";
const MAX_STEP = 5;

const createForm = () => ({
  companyName: "",
  country: "",
  industry: "",
  employeesRange: "",
  revenueRange: "",
  serviceCountries: [],
  objectives: [],
});

const clampStep = (value) => {
  const step = Number(value) || 1;
  return Math.min(Math.max(step, 1), MAX_STEP);
};

const resolvePayload = (payload) => {
  if (payload && typeof payload === "object" && "data" in payload) {
    return payload.data;
  }
  return payload;
};

const getCompanyId = (company) =>
  company?.id ?? company?._id ?? company?.companyId ?? null;

const buildCompanyFormPatch = (company) => {
  if (!company || typeof company !== "object") return null;
  const patch = {};
  const name = company.companyName ?? company.name;
  if (name) patch.companyName = name;
  if (company.country) patch.country = company.country;
  if (company.industry) patch.industry = company.industry;
  if (company.employeesRange) patch.employeesRange = company.employeesRange;
  if (company.revenueRange) patch.revenueRange = company.revenueRange;
  if (Array.isArray(company.serviceCountries))
    patch.serviceCountries = company.serviceCountries;
  if (Array.isArray(company.objectives)) patch.objectives = company.objectives;
  return Object.keys(patch).length > 0 ? patch : null;
};

class CompanyStore {
  storage = typeof window !== "undefined" ? window.sessionStorage : null;
  step = 1;
  loading = false;
  companyLoading = false;
  company = null;
  companies = [];
  companiesLoading = false;
  companiesError = "";
  companiesLoaded = false;
  form = createForm();
  _fetchPromise = null;
  _companiesPromise = null;

  constructor() {
    makeAutoObservable(this);
    this.loadDraft();
  }

  loadDraft = () => {
    try {
      const raw = this.storage?.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (!saved || typeof saved !== "object") return;
      if (typeof saved.step === "number") {
        this.step = Math.min(Math.max(saved.step, 1), 4);
      }
      if (saved.form && typeof saved.form === "object") {
        this.form = { ...this.form, ...saved.form };
      }
    } catch {
      // Ignore draft errors.
    }
  };

  getDraftOwnerId = () => {
    const user = authStore?.user;
    return user?.userId ?? user?.id ?? user?._id ?? user?.email ?? null;
  };

  setStep = (next) => {
    this.step = clampStep(next);
    this.saveDraft();
  };

  next = () => {
    this.setStep(Math.min(this.step + 1, MAX_STEP));
  };

  prev = () => {
    this.setStep(Math.max(this.step - 1, 1));
  };

  setField = (key, value) => {
    this.form = { ...this.form, [key]: value };
    this.saveDraft();
  };

  setForm = (patch) => {
    this.form = { ...this.form, ...patch };
    this.saveDraft();
  };

  hydrateForm = (patch) => {
    if (!patch || typeof patch !== "object") return;
    this.form = { ...this.form, ...patch };
  };

  setCompany = (nextCompany) => {
    this.company = nextCompany ?? null;
  };

  reset = (options = {}) => {
    const persist = options?.persist !== false;
    this.step = 1;
    this.form = createForm();
    if (persist) {
      this.saveDraft();
    }
  };

  useSampleData = () => {
    this.setForm({
      companyName: "Acme Inc.",
      country: "singapore",
      industry: "food_083",
      employeesRange: "51 - 200",
      revenueRange: "2M - 10M",
      serviceCountries: ["singapore", "taiwan"],
      objectives: ["electricity", "fuel"],
    });
  };

  validateStep = (step = this.step, form = this.form) =>
    validateCompanyFormStep(step, form);

  get fieldKeys() {
    return [
      "companyName",
      "country",
      "industry",
      "employeesRange",
      "revenueRange",
      "serviceCountries",
      "objectives",
    ];
  }

  get filledCount() {
    return this.fieldKeys.reduce((acc, key) => {
      const value = this.form[key];
      if (Array.isArray(value)) return acc + (value.length > 0 ? 1 : 0);
      if (typeof value === "boolean") return acc + (value ? 1 : 0);
      if (typeof value === "string")
        return acc + (value.trim().length > 0 ? 1 : 0);
      return acc;
    }, 0);
  }

  get totalCount() {
    return this.fieldKeys.length;
  }

  get progress() {
    return Math.round((this.filledCount / this.totalCount) * 100);
  }

  get progressGradient() {
    const progress = this.progress;
    const hue = Math.max(0, Math.min(120, Math.round((progress / 100) * 120)));
    const start = Math.max(0, hue - 25);
    const end = Math.min(140, hue + 10);
    return `linear-gradient(90deg, hsl(${start}, 92%, 55%), hsl(${end}, 85%, 45%))`;
  }

  get companyOptions() {
    if (!Array.isArray(this.companies)) return [];
    return this.companies
      .map((company) => ({
        value: getCompanyId(company) || "",
        label: company?.companyName || company?.name || "Company",
      }))
      .filter((option) => option.value);
  }

  get canContinue() {
    return Object.keys(this.validateStep()).length === 0;
  }

  saveDraft = () => {
    try {
      const ownerId = this.getDraftOwnerId();
      const draft = {
        step: Math.min(this.step, 4),
        form: this.form,
        ...(ownerId ? { ownerId } : {}),
      };
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(draft));
    } catch {
      // Ignore draft errors.
    }
  };

  syncDraftOwner = (ownerId = this.getDraftOwnerId()) => {
    if (!this.storage || !ownerId) return;
    try {
      const raw = this.storage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      const storedOwner = saved?.ownerId ?? null;
      if (!storedOwner || storedOwner !== ownerId) {
        this.clearDraft();
        this.reset({ persist: false });
      }
    } catch {
      this.clearDraft();
      this.reset({ persist: false });
    }
  };

  clearDraft = () => {
    try {
      this.storage?.removeItem(STORAGE_KEY);
    } catch {
      // Ignore storage failures.
    }
  };

  fetchCompany = async (input) => {
    const options =
      typeof input === "object" && input !== null && !Array.isArray(input)
        ? input
        : { companyId: input };

    const force = Boolean(options?.force);
    const overrideCompanyId =
      options?.companyId ?? options?.companyID ?? options?.id ?? null;

    if (!force && this._fetchPromise) {
      return this._fetchPromise;
    }

    const companyId =
      overrideCompanyId ??
      authStore?.user?.companyId ??
      getCompanyId(this.company);

    if (!companyId) {
      runInAction(() => {
        this.company = null;
        this.companyLoading = false;
      });
      return Promise.resolve(null);
    }

    runInAction(() => {
      this.companyLoading = true;
    });

    const request = companyService
      .getCompany(companyId, { throwOnError: false })
      .then((data) => {
        if (!data) {
          throw new Error("Unable to load company details.");
        }
        const payload = resolvePayload(data);
        const company = payload?.company ?? payload;
        const patch = buildCompanyFormPatch(company);
        runInAction(() => {
          if (patch) this.hydrateForm(patch);
          this.company = company || null;
        });
        return company;
      })
      .catch((error) => {
        runInAction(() => {
          this.company = null;
        });
        throw error;
      })
      .finally(() => {
        runInAction(() => {
          this.companyLoading = false;
        });
        this._fetchPromise = null;
      });

    this._fetchPromise = request;
    return request;
  };

  createCompany = async (companyId, options = {}) => {
    const { showToast: shouldToast = true, toastMessages = {} } = options;
    this.loading = true;

    try {
      const actionPromise = companyId
        ? companyService.updateCompany(companyId, this.form)
        : companyService.createCompany(this.form);

      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading: "Saving company...",
          success: "Company saved successfully.",
          error: (err) => err.message,
          ...toastMessages,
        });
      }

      const data = await actionPromise;
      const payload = resolvePayload(data);
      const company = payload?.company ?? payload;
      runInAction(() => {
        if (company && typeof company === "object") {
          this.company = company;
        }
      });

      if (!companyId) {
        await authStore.refreshMe();
      }

      return company;
    } catch (error) {
      const wrapped = new Error(
        error?.message || "Unable to save company details.",
      );
      wrapped.cause = error;
      throw wrapped;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
      this.clearDraft();
    }
  };

  fetchPublicCompanies = async (options = {}) => {
    const force = Boolean(options?.force);
    if (!force) {
      if (this._companiesPromise) return this._companiesPromise;
      if (this.companiesLoaded) return Promise.resolve(this.companies);
    }

    runInAction(() => {
      this.companiesLoading = true;
      this.companiesError = "";
      if (force) {
        this.companiesLoaded = false;
      }
    });

    const request = companyService
      .listPublicCompanies({ throwOnError: false })
      .then((data) => {
        if (!data) {
          throw new Error("Unable to load companies.");
        }
        const payload = resolvePayload(data) || {};
        const companies =
          payload?.companies ||
          payload?.data?.companies ||
          payload?.data ||
          payload;
        runInAction(() => {
          this.companies = Array.isArray(companies) ? companies : [];
          this.companiesLoaded = true;
        });
        return this.companies;
      })
      .catch((error) => {
        const message = error?.message || "Unable to load companies.";
        runInAction(() => {
          this.companies = [];
          this.companiesError = message;
          this.companiesLoaded = true;
        });
        throw error;
      })
      .finally(() => {
        runInAction(() => {
          this.companiesLoading = false;
        });
        this._companiesPromise = null;
      });

    this._companiesPromise = request;
    return request;
  };
}

export const companyStore = new CompanyStore();
